import { useState } from "react";

const WORD_TIERS = [
  {
    range: "300 字以下",
    type: "快讯短瓜",
    desc: "适合一句话能说清的小瓜、预告或澄清。信息完整即可，不硬凑字数。",
    icon: "ri-flashlight-fill",
    color: "text-primary-500 bg-primary-50",
  },
  {
    range: "300 – 800 字",
    type: "标准好瓜",
    desc: "最推荐的区间，能讲清起因、经过与结果，读起来不累，过审率最高。",
    icon: "ri-fire-fill",
    color: "text-secondary-500 bg-secondary-50",
    recommended: true,
  },
  {
    range: "800 – 1500 字",
    type: "深度长瓜",
    desc: "适合人物访谈、背后故事等有信息量的瓜。请用小标题分段，别让文字挤成一团。",
    icon: "ri-stack-fill",
    color: "text-accent-600 bg-accent-50",
  },
];

const COPYRIGHT_POINTS = [
  {
    icon: "ri-seedling-line",
    title: "原创界定",
    desc: "亲自见证、亲历或掌握一手资料的瓜料才算原创。转述他人消息请说明信息来源，不标注来源的转述容易被当作搬运。",
  },
  {
    icon: "ri-stackshare-line",
    title: "禁止搬运洗稿",
    desc: "直接照搬别家平台的文字、改动措辞重新发一遍的「洗稿」，或把零散信息拼凑成新瓜，均属违规，一经查实永久封禁投稿。",
  },
  {
    icon: "ri-copyright-line",
    title: "配图版权",
    desc: "配图请确保有使用权，注明出处或来源平台。使用他人拍摄的照片、表情包请谨慎，侵权风险由投稿人自行承担。",
  },
  {
    icon: "ri-eye-off-line",
    title: "隐私打码",
    desc: "涉及真实人物请使用化名，对头像、电话、住址、证件号等敏感信息一律打码，保护当事人是投稿的基本底线。",
  },
];

export default function WordCopyright() {
  const [activeTier, setActiveTier] = useState("300 – 800 字");
  const current = WORD_TIERS.find((t) => t.range === activeTier) ?? WORD_TIERS[0];

  return (
    <section id="word-copyright" className="scroll-mt-20 bg-background-100/60 py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-sm font-medium text-secondary-800">
          <i className="ri-scales-3-line"></i> 字数限制 · 原创与版权
        </span>
        <h2 className="mt-4 font-display text-3xl text-foreground-900 md:text-4xl">
          瓜有多长、权归谁，先说清楚
        </h2>

        <div className="mt-10 grid gap-6 lg:grid-cols-5">
          {/* 字数区间交互 */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 text-lg font-bold text-foreground-900">
                <i className="ri-ruler-2-fill text-secondary-500"></i> 字数三档怎么选
              </h3>
              <div className="mt-5 flex flex-wrap gap-2">
                {WORD_TIERS.map((tier) => (
                  <button
                    key={tier.range}
                    onClick={() => setActiveTier(tier.range)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      activeTier === tier.range
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {tier.range}
                  </button>
                ))}
              </div>

              <div key={current.range} className="animate-pop mt-6 rounded-xl bg-background-100/70 p-5">
                <div className="flex items-center gap-3">
                  <span className={`flex h-12 w-12 items-center justify-center rounded-xl text-2xl ${current.color}`}>
                    <i className={current.icon}></i>
                  </span>
                  <div>
                    <p className="text-sm text-foreground-600">{current.range}</p>
                    <p className="text-lg font-bold text-foreground-900">{current.type}</p>
                  </div>
                  {current.recommended && (
                    <span className="ml-auto rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-700">
                      最推荐
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm leading-6 text-foreground-700">{current.desc}</p>
              </div>

              <div className="mt-4 rounded-xl border border-dashed border-secondary-300 bg-secondary-50/60 p-4">
                <p className="flex items-start gap-2 text-sm leading-6 text-secondary-800">
                  <i className="ri-lightbulb-flash-fill mt-0.5"></i>
                  小提示：标题不计入正文字数，配图说明按行数估算。超过 1500 字的瓜料，请先拆分或精炼成单篇重点。
                </p>
              </div>
            </div>
          </div>

          {/* 原创版权 */}
          <div className="lg:col-span-3">
            <div className="grid h-full gap-4 sm:grid-cols-2">
              {COPYRIGHT_POINTS.map((point) => (
                <article
                  key={point.title}
                  className="rounded-2xl border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-100 text-xl text-accent-700">
                    <i className={point.icon}></i>
                  </span>
                  <h4 className="mt-3 font-bold text-foreground-900">{point.title}</h4>
                  <p className="mt-1.5 text-sm leading-6 text-foreground-700">{point.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}